import React, { useState, useEffect } from "react";
import { ColumnDef } from "@tanstack/react-table";
import styles from "./styles/policy.module.css";
import { Toaster } from "../../components/ui/toaster";
import { useToast } from "../../hooks/use-toast";
import DataTable from "../../shared/DataTable";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/Label";
import { Input } from "../../components/ui/input";
import { PolicyPageSchema } from "../../Valdations/Schema/group.schema";
import { useGroupForm } from "../../hooks/useGroupForm";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../components/ui/dialog";
import { isViewer } from "../../interface/RoleHelpers";
import { PolicyAxiosInstance } from "../../Network/policy";
import { Datum } from "../../interface/policy.types";
import FileUpload from "../../UploadFile/PolicyFileUpload";
import { AxiosError } from "axios";
import { AlertTriangle } from "lucide-react";
import { canRestoreOrHardDelete } from "../../interface/RoleHelpers";

const EXPORT_ROW_URL = (id: string | number) => `policies/${id}/export`;

const saveBlobResponse = (response: any, fallbackName: string) => {
  const disposition: string = response.headers?.["content-disposition"] ?? "";
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  const filename = match ? decodeURIComponent(match[1]) : fallbackName;

  const blob = new Blob([response.data], {
    type: response.headers?.["content-type"] || "application/octet-stream",
  });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};

const getBlobErrorMessage = async (error: any, fallback: string): Promise<string> => {
  const data = error?.response?.data;
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      return parsed?.error?.message || parsed?.message || fallback;
    } catch {
      return fallback;
    }
  }
  return data?.error?.message || data?.message || error?.message || fallback;
};

const PolicyPage: React.FC = () => {
  // =====================================
  // Main Data State
  // =====================================
  const [allItems, setAllItems] = useState<Datum[]>([]);


  // =====================================
  // Form Input State
  // =====================================
  const [isActive, setIsActive] = useState<boolean>(true);

  // =====================================
  // Pagination State
  // =====================================
  const [searchInput, setSearchInput] = useState(""); // Immediate input value typed by the user
  const [search, setSearch] = useState(""); // Debounced value sent to the server
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // =====================================
  // Form Control Helpers
  // =====================================
  const [submitted, setSubmitted] = useState(false);
  const { values, errors, setField, validateAll } = useGroupForm(PolicyPageSchema);

  // =====================================
  // Selected / Editing State
  // =====================================
  const [editIndex, setEditIndex] = useState<null | number>(null);

  // =====================================
  // Dialog & Modal Refs
  // =====================================
  const [warningDialog, setWarningDialog] = useState<{
    open: boolean;
    type: "delete" | "hardDelete" | null;
    item: Datum | null;
    usageData: any;
    loadingUsage: boolean;
  }>({
    open: false,
    type: null,
    item: null,
    usageData: null,
    loadingUsage: false,
  });
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Datum | null>(null);
  const [showAllLogs, setShowAllLogs] = useState(false);


  // =====================================
  // Search (server-side)
  // =====================================
  const [advancedSearchInput, setAdvancedSearchInput] = useState({
    name: "",
    policyId: "",
    isActive: "",
    isSoftDeleted: "false",
  });
  const [advancedSearch, setAdvancedSearch] = useState({
    name: "",
    policyId: "",
    isActive: "",
    isSoftDeleted: "false",
  });

  const { toast } = useToast();

  const handleOpenDialog = async (item: Datum) => {
    try {
      const response = await PolicyAxiosInstance.get(`policies/${item.id}`);
      const fullData = response.data.data || response.data;
      setSelectedItem(fullData);
      setIsViewDialogOpen(true);
    } catch (error) {
      console.error("Failed to fetch group details:", error);
      toast({
        title: "Error",
        description: "Failed to load group details. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleCloseDialog = () => {
    setIsViewDialogOpen(false);
    setSelectedItem(null);
    setSubmitted(false);
    clear();
    setShowAllLogs(false);
  };

  // =====================================
  // Form Utilities
  // =====================================
  const clear = () => {
    setSubmitted(false);
    setField("policyName", "");
    setField("policyId", "");
    setIsActive(true);
    setField("startDate", "");
    setField("endDate", "");
  };

  const handleAdd = () => {
    setEditIndex(null);
    setField("policyName", "");
    setField("policyId", "");
    setIsDialogOpen(true);
    setField("startDate", "");
    setField("endDate", "");
  };

  // ── UTC date helper ──────────────────────────────────────────────────────
  const toUtcDate = (dateStr: string): string => {
    const [year, month, day] = dateStr.split("-").map(Number);
    return new Date(Date.UTC(year, month - 1, day)).toISOString().split("T")[0];
  };

  const getCreatorName = (item: any): string | null => {
    if (!item) return null;
    if (item.createdBy) {
      const match = item.auditLog?.find((log: any) => log.actorId === item.createdBy);
      if (match) return match.actorName;
      return item.createdBy;
    }
    return null;
  };

  // =====================================
  // Error Handling
  // =====================================
  const getApiErrorMessage = (error: any): string => {
    if (!error) return "An unknown error occurred";

    const responseData = error.response?.data;

    if (responseData?.error?.details && Array.isArray(responseData.error.details)) {
      return responseData.error.details[0].message;
    }

    if (responseData?.error?.message) return responseData.error.message;
    if (responseData?.message) return responseData.message;

    return error.message || "Something went wrong";
  };

  // =====================================
  // Filters (shared by the table query and "Export all")
  // =====================================
  const buildFilterParams = (): Record<string, any> => {
    const params: Record<string, any> = {};

    if (search) {
      params.policyName = search;
    }
    if (advancedSearch.name) {
      params.policyName = advancedSearch.name;
    }
    if (advancedSearch.policyId !== "") {
      params.policyId = advancedSearch.policyId;
    }
    if (advancedSearch.isActive) {
      params.isActive = advancedSearch.isActive === "Active";
    }
    if (advancedSearch.isSoftDeleted !== "") {
      params.isSoftDeleted = advancedSearch.isSoftDeleted === "true";
    }

    return params;
  };

  const hasActiveFilters = Boolean(
    search ||
      advancedSearch.isActive ||
      advancedSearch.isSoftDeleted === "true" ||
      advancedSearch.name ||
      advancedSearch.policyId,
  );

  // =====================================
  // Fetch Items (Server-side pagination + search + filters)
  // =====================================
  const fetchItems = async () => {
    try {
      const params: Record<string, any> = {
        page,
        limit,
        ...buildFilterParams(),
      };

      const res = await PolicyAxiosInstance.get("policies", { params });

      const responseData = res.data.data;
      const items = responseData?.items ?? [];

      setAllItems(items);
      setTotalPages(responseData?.totalPages ?? 1);
      setTotalItems(responseData?.total ?? items.length);

      const pages = responseData?.totalPages ?? 1;
      if (page > pages) {
        setPage(Math.max(1, pages));
      }
    } catch (error) {
      console.error(error);
      toast({
        title: "Error",
        description: getApiErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  // =====================================
  // CRUD Functions
  // =====================================
  const addItem = async (payload: {
    policyName: string;
    policyId: string;
    isActive: boolean;
    startDate: string;
    endDate: string;
  }) => {
    try {
      const response = await PolicyAxiosInstance.post("policies", payload);

      if (response.data?.success) {
        fetchItems();
        setIsDialogOpen(false);
        clear();
        toast({
          variant: "success",
          title: "Success",
          description: "New Policy Added Successfully.",
        });
      }
    } catch (error: any) {
      const message = getApiErrorMessage(error);
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
      console.error("Add Policy Error:", error);
    }
  };

  const handleSubmit = async () => {
    setSubmitted(true);
    if (!validateAll()) return;

    const payload = {
      policyName: values.policyName,
      policyId: values.policyId,
      startDate: toUtcDate(values.startDate),
      endDate: toUtcDate(values.endDate),
      isActive: isActive,
    };

    try {
      await addItem(payload);
    } catch (error) {
      const message = getApiErrorMessage(error);
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    }
  };

  const handleEdit = (item: Datum) => {
    setField("policyName", item.policyName);
    setField("policyId", item.policyId);
    setIsActive(item.isActive);
    setField("startDate", item.startDate ? item.startDate.split("T")[0] : "");
    setField("endDate", item.endDate ? item.endDate.split("T")[0] : "");
    setEditIndex(item.id);
    setIsDialogOpen(true);
  };

  const updateItem = async (id: string | number) => {
    if (!validateAll()) return;

    const payload = {
      policyName: values.policyName,
      policyId: values.policyId,
      startDate: toUtcDate(values.startDate),
      endDate: toUtcDate(values.endDate),
      isActive: isActive,
    };

    try {
      const response = await PolicyAxiosInstance.put(`policies/${id}`, payload);

      if (response.data?.success) {
        fetchItems();
        setIsDialogOpen(false);
        clear();
        toast({
          variant: "success",
          title: "Success",
          description: "Policy Updated Successfully.",
        });
      }
    } catch (error: AxiosError | any) {
      const message = getApiErrorMessage(error);
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
      console.error(error);
    }
  };

  const removeItem = async (id: string | number): Promise<void> => {
    try {
      await PolicyAxiosInstance.delete(`policies/${id}/soft-delete`);
      fetchItems();
      toast({
        variant: "success",
        title: "Success",
        description: "Policy Deleted Successfully.",
      });
    } catch (error) {
      const message = getApiErrorMessage(error);
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    }
  };

  const hardDeleteItem = async (id: string | number): Promise<void> => {
    try {
      await PolicyAxiosInstance.delete(`policies/${id}`);
      await fetchItems();
      toast({
        variant: "success",
        title: "Success",
        description: "Group permanently deleted.",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: getApiErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  const restoreItem = async (id: string | number): Promise<void> => {
    try {
      await PolicyAxiosInstance.patch(`policies/${id}/restore`);
      await fetchItems();
      toast({
        variant: "success",
        title: "Success",
        description: "Group restored successfully.",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: getApiErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  const toggleActive = async (row: Datum) => {
    if (row.isSoftDeleted) return;
    const payload = {
      policyName: row.policyName,
      policyId: row.policyId,
      startDate: row.startDate,
      endDate: row.endDate,
      isActive: !row.isActive
    };

    try {
      await PolicyAxiosInstance.put(`policies/${row.id}`, payload);

      toast({
        variant: "success",
        title: "Success",
        description: `Status Updated to ${!row.isActive ? "Active" : "Inactive"}`,
      });

      setAllItems((prev) =>
        prev.map((item) =>
          item.id === row.id ? { ...item, isActive: !item.isActive } : item,
        ),
      );
    } catch (error: any) {
      const message = getApiErrorMessage(error);
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    }
  };

  // =====================================
  // Debounce
  // =====================================
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    const t = setTimeout(() => {
      setAdvancedSearch({
        name: advancedSearchInput.name.trim(),
        policyId: advancedSearchInput.policyId.trim(),
        isActive: advancedSearchInput.isActive,
        isSoftDeleted: advancedSearchInput.isSoftDeleted,
      });
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [advancedSearchInput]);

  // =====================================
  // Fetch on page / limit / search / filters change (server-side, single source of truth)
  // =====================================
  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, search, advancedSearch]);

  // =====================================
  // Export ONE policy (handled by the backend)
  // =====================================
  const handleDownload = async (item: Datum) => {
    if (isViewer()) {
      toast({
        title: "Error",
        description: "Access denied. Viewers are only allowed to view the system",
        variant: "destructive",
      });
      return;
    }

    try {
   
      const response = await PolicyAxiosInstance.get(EXPORT_ROW_URL(item.id), {
        responseType: "blob",
      });
      saveBlobResponse(response, `Policy_${item.policyId || "export"}.xlsx`);
      toast({
        variant: "success",
        title: "Export Finished",
        description: `"${item.policyName}" exported successfully.`,
      });
    } catch (error: any) {
      toast({
        title: "Export Failed",
        description: await getBlobErrorMessage(error, "Failed to export policy."),
        variant: "destructive",
      });
    } 
  };

  // Table columns
  const columns: ColumnDef<Datum>[] = [
    {
      accessorKey: "policyName",
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="w-full font-bold text-black justify-center"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Policy Name
          {column.getIsSorted() === "asc"
            ? " ↑"
            : column.getIsSorted() === "desc"
              ? " ↓"
              : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const item = row.original;
        return (
          <div data-testid={`policy-name-${item.id}`} className={`text-center ${item.isSoftDeleted ? "line-through text-slate-400" : ""}`}>
            {row.getValue("policyName")}
          </div>
        );
      },
    },
    {
      accessorKey: "policyId",
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="w-full font-bold text-black justify-center"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Policy ID
          {column.getIsSorted() === "asc"
            ? " ↑"
            : column.getIsSorted() === "desc"
              ? " ↓"
              : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const item = row.original;
        return (
          <div data-testid={`policy-id-${item.id}`} className={`text-center ${item.isSoftDeleted ? "line-through text-slate-400" : ""}`}>
            {row.getValue("policyId")}
          </div>
        );
      },
    },
    {
      accessorKey: "isActive",
      header: () => (
        <div className="w-full font-bold text-black text-center normal-case">
          Status
        </div>
      ),
      cell: ({ row }) => {
        const item = row.original;
        if (item.isSoftDeleted) {
          return (
            <div className="w-full flex justify-center">
              <div className="relative inline-flex h-6 w-11 items-center rounded-full bg-gray-200 opacity-40 cursor-not-allowed">
                <span className="inline-block h-4 w-4 transform rounded-full bg-white translate-x-1" />
              </div>
            </div>
          );
        }
        return (
          <div className="w-full flex justify-center">
            <button
              data-testid={`toggle-active-${item.id}`}
              onClick={() => toggleActive(item)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                item.isActive ? "bg-green-500" : "bg-gray-300"
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  item.isActive ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
          </div>
        );
      },
    },
    {
      id: "view",
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="w-full font-bold text-black text-center"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          View
          {column.getIsSorted() === "asc"
            ? " ↑"
            : column.getIsSorted() === "desc"
              ? " ↓"
              : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const item = row.original;
        return (
          <div className="w-full flex justify-center py-2">
            <button
              data-testid={`btn-view-${item.id}`}
              onClick={() => handleOpenDialog(item)}
              className="text-blue-600 hover:text-blue-800"
              title="View Details"
            >
              <i className="fa-solid fa-eye text-lg"></i>
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <div data-testid="policy-page-container">
      <div className={styles.container} data-testid="policy-page-container">
        <h5 className={styles.title} data-testid="policy-page-title">
          <span
            className={styles.dialogIcon}
            data-testid="policy-page-title-icon"
          >
            <i className="fa-solid fa-shield-halved"></i>
          </span>
          Policy 
        </h5>

        <FileUpload
          fetchItems={fetchItems}
          totalItems={totalItems}
          hasFilters={hasActiveFilters}
          exportParams={buildFilterParams()}
        />
      </div>

      {/* search */}
      <div
        className={`${styles["compact-search-container"]} ${styles["mb-6"]}`}
        data-testid="policy-page-search-container"
      >
        <div className={styles["search-wrapper"]}>
          <div className={styles["search-header-compact"]}>
            <div className={styles["header-info"]}>
              <i className="fa-solid fa-filter"></i>
              <span>Search Filters</span>
            </div>

            <button
              data-testid="policy-page-btn-reset-filters"
              onClick={() => {
                setSearchInput("");
                setAdvancedSearchInput({
                  name: "",
                  policyId: "",
                  isActive: "",
                  isSoftDeleted: "false",
                });
              }}
              className={styles["btn-clear-compact"]}
            >
              <i className="fa-solid fa-rotate-left"></i>
              Reset
            </button>
          </div>

          <div
            className={styles["search-fields-compact"]}
            style={{ gridTemplateColumns: "repeat(4, 1fr)" }}
          >
            {/* Policy Name */}
            <div className={styles["field-compact"]} data-testid="policy-page-search-policyname">
              <label className={styles["label-compact"]}>
                <i className="fa-solid fa-layer-group"></i>
                Policy Name
              </label>

              <div className={styles["input-container-compact"]}>
                <input
                  data-testid="policy-page-input-search-policyname"
                  type="text"
                  placeholder="Search by Policy Name..."
                  value={advancedSearchInput.name}
                  onChange={(e) =>
                    setAdvancedSearchInput({
                      ...advancedSearchInput,
                      name: e.target.value,
                    })
                  }
                  className={styles["input-compact"]}
                />
              </div>
            </div>

            {/* Policy ID */}
            <div className={styles["field-compact"]} data-testid="policy-page-search-policyid">
              <label className={styles["label-compact"]}>
                <i className="fa-solid fa-layer-group"></i>
                Policy ID
              </label>

              <div className={styles["input-container-compact"]}>
                <input
                  data-testid="policy-page-input-search-policyid"
                  type="text"
                  placeholder="Filter by Policy ID..."
                  value={advancedSearchInput.policyId ?? ""}
                  onChange={(e) =>
                    setAdvancedSearchInput({
                      ...advancedSearchInput,
                      policyId: e.target.value,
                    })
                  }
                  className={styles["input-compact"]}
                />
              </div>
            </div>

            {/* Status */}
            <div className={styles["field-compact"]} data-testid="policy-page-search-isActive">
              <label className={styles["label-compact"]}>
                <i className="fa-solid fa-toggle-on"></i>
                Status
              </label>
              <div className={styles["select-container-compact"]}>
                <select
                  data-testid="policy-page-select-search-status"
                  value={advancedSearchInput.isActive}
                  onChange={(e) =>
                    setAdvancedSearchInput({
                      ...advancedSearchInput,
                      isActive: e.target.value,
                    })
                  }
                  className={styles["input-compact"]}
                >
                  <option value="">All Status</option>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
            </div>

            {/* Deleted Groups */}
            <div
              className={styles["field-compact"]}
              data-testid="policy-page-search-soft-deleted"
            >
              <label className={styles["label-compact"]}>
                <i className="fa-solid fa-trash-can"></i>
                Deleted policies
              </label>
              <button
                data-testid="policy-page-btn-toggle-soft-deleted"
                type="button"
                onClick={() =>
                  setAdvancedSearchInput({
                    ...advancedSearchInput,
                    isSoftDeleted: advancedSearchInput.isSoftDeleted === "true" ? "false" : "true",
                  })
                }
                className={`${styles["input-compact"]} w-full flex items-center justify-center gap-2 cursor-pointer select-none transition-all ${
                  advancedSearchInput.isSoftDeleted === "true"
                    ? "!bg-red-500 !border-red-600 !text-white font-semibold"
                    : "hover:bg-slate-50 text-slate-600"
                }`}
              >
                <i
                  className={`fa-solid ${
                    advancedSearchInput.isSoftDeleted === "true" ? "fa-rotate-left" : "fa-eye-slash"
                  } text-xs`}
                ></i>
                <span className="text-xs">
                  {advancedSearchInput.isSoftDeleted === "true" ? "Back to List" : "Show "}
                </span>
              </button>
            </div>
          </div>

          <div className={styles["stats-compact"]}>
            <i className="fa-solid fa-chart-bar"></i>
            <span className={styles["stats-text-compact"]} data-testid="policy-page-stats">
              Showing <strong>{allItems.length}</strong> of <strong>{totalItems}</strong> groups
            </span>
            {(advancedSearch.isActive ||
              advancedSearch.isSoftDeleted == "true" ||
              advancedSearch.name ||
              advancedSearch.policyId) && (
              <span className={styles["badge-filtered-compact"]}>
                <i className="fa-solid fa-filter"></i> Filtered
              </span>
            )}
          </div>
        </div>
      </div>

      {isViewDialogOpen && selectedItem && (
        <dialog
          className={styles["modern-dialog"]}
          open
          data-testid="policy-page-dialog"
        >
          <div
            className={styles["dialog-container"]}
            data-testid="policy-page-dialog-container"
          >
            {/* Header */}
            <div
              className={styles["dialog-header"]}
              data-testid="policy-page-dialog-header"
            >
              <div className={styles["header-content"]}>
                <div
                  className={styles["header-icon"]}
                  data-testid="policy-page-dialog-header-icon"
                >
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                    <path
                      d="M3.27 6.96L12 12.01l8.73-5.05M12 22.08V12"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                  </svg>
                </div>
                <div>
                  <h2
                    className={styles["dialog-title"]}
                    data-testid="policy-page-dialog-title"
                  >
                    Policy Details
                  </h2>
                  <p
                    className={styles["dialog-subtitle"]}
                    data-testid="policy-page-dialog-subtitle"
                  >
                    {selectedItem.policyName}
                  </p>
                </div>
              </div>

              <button
                className={styles["close-btn"]}
                onClick={handleCloseDialog}
                data-testid="policy-page-dialog-close-btn"
              >
                ✕
              </button>
            </div>

            {/* Body */}
            <div
              className={styles["dialog-body"]}
              data-testid="policy-page-dialog-body"
            >
              <section
                className={styles.section}
                data-testid="policy-page-dialog-basic-info"
              >
                <h3 className={styles["section-title"]}>Basic Information</h3>

                <div className={styles["info-group"]}>
                  <div
                    className={styles["info-row"]}
                    data-testid="policy-page-dialog-row-id"
                  >
                    <span className={styles["row-label"]}>Policy ID</span>
                    <span className={styles["info-text"]}>
                      {selectedItem
                        ? (page - 1) * limit +
                          allItems.findIndex(
                            (i) => i.id === selectedItem.id,
                          ) +
                          1
                        : ""}
                    </span>
                  </div>

                  <div
                    className={styles["info-row"]}
                    data-testid="policy-page-dialog-row-name"
                  >
                    <span className={styles["row-label"]}>Policy Name</span>
                    <span className={styles["info-text"]}>
                      {selectedItem.policyName}
                    </span>
                  </div>
                  <div
                    className={styles["info-row"]}
                    data-testid="policy-page-dialog-row-id"
                  >
                    <span className={styles["row-label"]}>Policy ID</span>
                    <span className={styles["info-text"]}>
                      {selectedItem.policyId}
                    </span>
                  </div>
                  <div
                    className={styles["info-row"]}
                    data-testid="policy-page-dialog-row-StartDate"
                  >
                    <span className={styles["row-label"]}>Start Date</span>
                    <span className={styles["info-text"]}>
                      {selectedItem.startDate ? selectedItem.startDate.split("T")[0] : ""}
                    </span>
                  </div>
                  <div
                    className={styles["info-row"]}
                    data-testid="policy-page-dialog-row-EndDate"
                  >
                    <span className={styles["row-label"]}>End Date</span>
                    <span className={styles["info-text"]}>
                      {selectedItem.endDate ? selectedItem.endDate.split("T")[0] : ""}
                    </span>
                  </div>

                  <div
                    className={styles["info-row"]}
                    data-testid="policy-page-dialog-row-isActive"
                  >
                    <span className={styles["row-label"]}>Status</span>
                    <div
                      className={`${styles["isActive-badge-inline"]} ${
                        selectedItem.isActive ? styles.active : styles.inactive
                      }`}
                      data-testid="policy-page-dialog-isActive-badge"
                    >
                      <span className={styles["isActive-dot"]}></span>
                      {selectedItem.isActive ? "Active" : "Inactive"}
                    </div>
                  </div>
                </div>
              </section>

              {/* ── Audit Trail / Logs ── */}
              <div style={{ padding: "20px 28px 8px" }} data-testid="policy-page-dialog-audit-trail">
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                  <div style={{ width: 28, height: 28, borderRadius: 8, background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #bbf7d0" }}>
                    <i className="fa-solid fa-clock-rotate-left" style={{ fontSize: 12, color: "#16a34a" }} />
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Audit Trail</span>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 16 }}>
                  <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px" }}>
                    <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8" }}>Created By</span>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10 }}>
                      <div style={{ width: 32, height: 32, borderRadius: "50%", background: "linear-gradient(135deg, #3b82f6, #2563eb)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: "#fff" }}>
                        {String(getCreatorName(selectedItem) ?? "?")[0].toUpperCase()}
                      </div>
                      <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{getCreatorName(selectedItem) || "—"}</span>
                    </div>
                  </div>

                  <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px" }}>
                    <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8" }}>Created On</span>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
                      <i className="fa-regular fa-calendar" style={{ fontSize: 14, color: "#3b82f6" }} />
                      <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>
                        {(selectedItem as any).createdAt
                          ? new Date((selectedItem as any).createdAt).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
                          : "—"}
                      </span>
                    </div>
                  </div>

                  <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px" }}>
                    <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8" }}>Last Updated</span>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
                      <i className="fa-regular fa-clock" style={{ fontSize: 14, color: "#10b981" }} />
                      <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>
                        {(selectedItem as any).updatedAt
                          ? new Date((selectedItem as any).updatedAt).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
                          : "—"}
                      </span>
                    </div>
                  </div>
                </div>

                {(selectedItem as any).auditLog?.length > 0 && (
                  <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12 }}>
                      <i className="fa-solid fa-pen-to-square" style={{ fontSize: 11, color: "#10b981" }} />
                      <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase" }}>Update History</span>
                      <span style={{ fontSize: 10, padding: "1px 8px", borderRadius: 9999, background: "#f0fdf4", color: "#15803d", fontWeight: 600, border: "1px solid #bbf7d0", marginLeft: "auto" }}>
                        {(selectedItem as any).auditLog.length} update{(selectedItem as any).auditLog.length !== 1 ? "s" : ""}
                      </span>
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {(selectedItem as any).auditLog
                        .slice(0, showAllLogs ? undefined : 4)
                        .map((log: any, i: number) => (
                          <div key={log.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", borderRadius: 8, background: i === 0 ? "#f0fdf4" : "#f8fafc", border: `1px solid ${i === 0 ? "#bbf7d0" : "#e2e8f0"}` }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <div style={{ width: 28, height: 28, borderRadius: "50%", background: i === 0 ? "linear-gradient(135deg, #10b981, #059669)" : "linear-gradient(135deg, #94a3b8, #64748b)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#fff" }}>
                                {String(log.actorName ?? "?")[0].toUpperCase()}
                              </div>
                              <span style={{ fontSize: 12, fontWeight: 600, color: "#1e293b" }}>{log.actorName}</span>
                              {i === 0 && (
                                <span style={{ fontSize: 10, padding: "1px 8px", borderRadius: 9999, background: "#dcfce7", color: "#15803d", fontWeight: 600, border: "1px solid #bbf7d0" }}>
                                  Latest
                                </span>
                              )}
                            </div>
                            <span style={{ fontSize: 11, color: "#64748b", fontWeight: 500 }}>
                              {new Date(log.createdAt).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                            </span>
                          </div>
                        ))}

                      {(selectedItem as any).auditLog.length > 4 && (
                        <div style={{ textAlign: "center", padding: "6px 0" }}>
                          <button
                            data-testid="policy-page-dialog-btn-toggle-logs"
                            onClick={() => setShowAllLogs((v) => !v)}
                            style={{ fontSize: 11, color: "#3b82f6", fontWeight: 600, background: "none", border: "none", cursor: "pointer" }}
                          >
                            {showAllLogs ? "Show less ↑" : `+${(selectedItem as any).auditLog.length - 4} more updates ↓`}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div
              className={styles["dialog-footer"]}
              data-testid="policy-page-dialog-footer"
            >
           
              <button
                className={styles["btn-secondary"]}
                onClick={handleCloseDialog}
                data-testid="policy-page-dialog-footer-close-btn"
              >
                Close
              </button>
            </div>
          </div>
        </dialog>
      )}

      {/* Delete & Permanent Delete Warning Dialog */}
      <Dialog
        data-testid="policyDeleteDialog"
        open={warningDialog.open}
        onOpenChange={(open) =>
          !open &&
          setWarningDialog({
            open: false,
            type: null,
            item: null,
            usageData: null,
            loadingUsage: false,
          })
        }
      >
        <DialogContent
          className="max-w-md bg-white p-0 overflow-hidden rounded-2xl flex flex-col max-h-[90vh]"
          data-testid="page-delete-warning-dialog-content"
        >
          {/* Header Section */}
          <div className="p-6 pb-2 flex-shrink-0">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 font-bold text-red-600">
                <AlertTriangle className="h-5 w-5" />
                {warningDialog.type === "hardDelete" ? "Permanent Deletion" : "Confirm Deletion"}
              </DialogTitle>
              <DialogDescription className="text-slate-500 text-sm mt-1">
                {warningDialog.type === "hardDelete"
                  ? "This will permanently remove the item from the system. This action cannot be undone."
                  : "Are you sure you want to delete this item? This action cannot be undone."}
              </DialogDescription>
            </DialogHeader>
          </div>

          {/* Dynamic Content Body */}
          <div className="px-6 flex-1 min-h-0 flex flex-col overflow-y-auto">
            {/* 1. Hard Delete Visual Warning */}
            {warningDialog.type === "hardDelete" && (
              <div className="py-6 flex flex-col items-center justify-center gap-3">
                <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center border-2 border-red-100">
                  <i className="fa-solid fa-trash-can text-red-500 text-xl"></i>
                </div>
                <p className="text-sm text-slate-600 text-center px-4">
                  The item{" "}
                  <strong className="text-slate-800">
                    "{warningDialog.item?.policyName || "this item"}"
                  </strong>{" "}
                  will be permanently deleted.
                </p>
              </div>
            )}

            {/* 2. Soft Delete (Regular Delete) */}
            {warningDialog.type === "delete" && (
              <div className="py-10 text-center flex flex-col items-center justify-center">
                <div className="bg-slate-50 p-4 rounded-full mb-4">
                  <AlertTriangle className="h-8 w-8 text-slate-300" />
                </div>
                <p className="text-slate-600 text-sm px-4">
                  Are you sure you want to delete{" "}
                  <strong className="text-slate-800">
                    "{warningDialog.item?.policyName || "this item"}"
                  </strong>
                  ? This item will be soft-deleted and can be restored later.
                </p>
              </div>
            )}
          </div>

          {/* Footer Actions Section */}
          <div className="p-6 pt-4 flex-shrink-0 bg-white border-t mt-auto">
            <DialogFooter className="flex flex-row gap-3 justify-center">
              <Button
                data-testid="warning-dialog-btn-cancel"
                variant="outline"
                className="flex-1 rounded-xl h-10 text-sm"
                onClick={() => setWarningDialog((prev) => ({ ...prev, open: false }))}
              >
                Cancel
              </Button>
              <Button
                data-testid="warning-dialog-btn-confirm"
                className="flex-1 rounded-xl h-10 text-white font-bold text-sm bg-red-500 hover:bg-red-600"
                onClick={async () => {
                  const itemId = warningDialog.item?.id;
                  if (itemId) {
                    if (warningDialog.type === "hardDelete") {
                      await hardDeleteItem(itemId);
                    } else {
                      await removeItem(itemId); // soft delete
                    }
                  }
                  setWarningDialog((prev) => ({ ...prev, open: false }));
                }}
              >
                {warningDialog.type === "hardDelete" ? "Yes, Permanently Delete" : "Yes, Delete"}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            clear();
            setEditIndex(null);
          }
          setIsDialogOpen(open);
        }}
      >
        <DialogContent className={styles.dialogContent} data-testid="policy-page-dialog-content">
          <DialogHeader className={styles.dialogHeader}>
            <DialogTitle className={styles.dialogTitle} data-testid="policy-form-dialog-title">
              {editIndex === null ? "Add Policy " : "Edit Policy "}
            </DialogTitle>
            <DialogDescription className={styles.dialogDescription}>
              Fill in the details below to {editIndex === null ? "create" : "update"} a Policy .
            </DialogDescription>
          </DialogHeader>

          {/* Body */}
          <div className={styles.dialogBody} data-testid="policy-page-dialog-body">
            <div className={styles.formGrid}>
              {/* Column 1 */}
              <div>
                <div className={styles.formGroup} data-testid="policy-page-input-groupname">
                  <Label htmlFor="name">Name :</Label>
                  <Input
                    data-testid="policy-form-input-policyname"
                    placeholder="Enter Name..."
                    value={values.policyName}
                    onChange={(e) => setField("policyName", e.target.value)}
                  />
                  {submitted && errors.policyName && (
                    <span className={styles.error} data-testid="policy-form-error-policyname">{errors.policyName}</span>
                  )}
                </div>
                <div className={styles.formGroup} data-testid="policy-page-input-startDate">
                  <Label htmlFor="startDate">Start Date</Label>
                  <Input
                    data-testid="policy-form-input-startdate"
                    id="startDate"
                    type="date"
                    value={values.startDate}
                    onChange={(e) => setField("startDate", e.target.value)}
                  />
                  {submitted && errors.startDate && (
                    <span className={styles.error} data-testid="policy-form-error-startdate">{errors.startDate}</span>
                  )}
                </div>
              </div>

              {/* Column 2 */}
              <div>
                <div className={styles.formGroup} data-testid="policy-page-input-policyId">
                  <Label htmlFor="PolicyID">Policy ID : </Label>
                  <Input
                    data-testid="policy-form-input-policyid"
                    type="text"
                    inputMode="numeric"
                    placeholder="Enter Policy ID..."
                    value={values.policyId}
                    onChange={(e) => setField("policyId", e.target.value)}
                  />
                  {submitted && errors.policyId && (
                    <span className={styles.error} data-testid="policy-form-error-policyid">{errors.policyId}</span>
                  )}
                </div>
                <div className={styles.formGroup} data-testid="policy-page-input-endDate">
                  <Label htmlFor="endDate">End Date</Label>
                  <Input
                    data-testid="policy-form-input-enddate"
                    id="endDate"
                    type="date"
                    value={values.endDate}
                    onChange={(e) => setField("endDate", e.target.value)}
                  />
                  {submitted && errors.endDate && (
                    <span className={styles.error} data-testid="policy-form-error-enddate">{errors.endDate}</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <DialogFooter className={styles.dialogFooter}>
            <Button
              data-testid="policy-form-btn-submit"
              onClick={() => {
                if (editIndex === null) {
                  handleSubmit();
                } else {
                  updateItem(editIndex);
                }
              }}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {editIndex === null ? "Add Policy" : "Update Policy"}
            </Button>

            <Button
              data-testid="policy-form-btn-cancel"
              className={styles.cancel}
              variant="outline"
              onClick={() => {
                setIsDialogOpen(false);
                setEditIndex(null);
                setSubmitted(false);
                clear();
              }}
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Toaster data-testid="policy-page-toaster" />

      <div data-testid="policy-page-table-wrapper">
        <DataTable
          onAdd={handleAdd}
          onEdit={(item: any) => {
            if (item.isSoftDeleted) return;
            handleEdit(item);
          }}
          onDelete={(id) => {
            const item = allItems.find((i) => i.id === id) ?? null;
            if (item?.isSoftDeleted) return;
            setWarningDialog({
              open: true,
              type: "delete",
              item,
              usageData: null,
              loadingUsage: false,
            });
          }}
          data={allItems}
          columns={columns}
          onDownload={handleDownload}
          addButtonLabel="Add New Policy"
          pageIndex={page - 1}
          pageSize={limit}
          totalPages={totalPages}
          onPageChange={(newPage) => setPage(newPage + 1)}
          onPageSizeChange={(size) => {
            setLimit(size);
            setPage(1);
          }}
          globalSearch={searchInput}
          onGlobalSearchChange={setSearchInput}
          recoveryActions={(item: any) => {
            if (!canRestoreOrHardDelete()) {
              return (
                <span className="inline-flex items-center gap-1 text-[10px] font-bold text-slate-400 bg-slate-50 border border-slate-200 px-2 py-1 rounded-lg">
                  <i className="fa-solid fa-lock text-[15px]"></i>
                </span>
              );
            }
            return (
              <div className="flex items-center justify-center gap-1.5">
                <button
                  data-testid={`btn-restore-${item.id}`}
                  onClick={() => restoreItem(item.id)}
                  className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 transition-all"
                >
                  <i className="fa-solid fa-rotate-left text-[9px]"></i>
                  Restore
                </button>
                <button
                  data-testid={`btn-hard-delete-${item.id}`}
                  onClick={() => setWarningDialog({
                    open: true,
                    type: "hardDelete",
                    item,
                    usageData: null,
                    loadingUsage: false,
                  })}
                  className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-bold bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 transition-all"
                >
                  <i className="fa-solid fa-trash text-[9px]"></i>
                  Delete
                </button>
              </div>
            );
          }}
          data-testid="policy-page-datatable"
        />
      </div>
    </div>
  );
};

export default PolicyPage;